import React, { useState, useRef, useEffect } from "react";
import { Search, Send, Sparkles, Boxes } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TaskSearchBar = ({ onSearch, initialQuery = "" , isLoading}) => {
  const [query, setQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef(null);

  const handleSearch = () => {
    if (query.trim() !== "") {
      onSearch(query);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  };

  // Update the query state when initialQuery prop changes
  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  // Adjust height whenever query changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [query]);

  const containerVariants = {
    focus: { 
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      borderColor: "rgba(96, 165, 250, 0.7)",
      scale: 1.02,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    },
    blur: { 
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      borderColor: "rgba(255, 255, 255, 0)",
      scale: 1,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  return (
    <motion.div 
      className="relative w-full max-w-2xl border rounded-2xl overflow-hidden bg-white"
      initial="blur"
      animate={isFocused ? "focus" : "blur"}
      variants={containerVariants}
    >
      {/* Decorative Gradient */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-blue-50 to-blue-100 z-0"
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: isFocused ? 0.5 : 0
        }}
        transition={{ duration: 0.3 }}>
          
      </motion.div>

      <motion.div 
        className="relative z-10 flex items-center p-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}>
        <textarea
          ref={textareaRef}
          rows={4}
          placeholder="What do you need? (e.g. 'I want to create a video...'), give as much details as you can to get better result!"
          className="w-full p-2 pr-12 outline-none bg-transparent text-gray-700 
            resize-none overflow-y 
            text-base leading-relaxed 
            placeholder-gray-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />

        {/* Search Button */}
        {!isLoading && (
        <motion.button
          onClick={handleSearch} 
          disabled={query.trim() === ""}
          className={`absolute right-2 top-1/2 -translate-y-1/2 
            p-2 rounded-full
            ${query.trim() === "" 
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 text-white'
            }`}
          whileHover={query.trim() !== "" ? { 
            scale: 1.1,
            backgroundColor: "#2563eb"
          } : {}}
          whileTap={query.trim() !== "" ? { 
            scale: 0.95 
          } : {}}
          transition={{ 
            type: "spring",
            stiffness: 400,
            damping: 10
          }}>
          <Send className="w-5 h-5" />
        </motion.button>
        )} 

        {isLoading && (
        <motion.button
          onClick={handleSearch} 
          disabled={query.trim() === ""}
          className={`absolute right-2 top-1/2 -translate-y-1/2 
            p-2 rounded-full
            ${query.trim() === "" 
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
              : 'bg-blue-900 text-white'
            }`}
            whileHover={query.trim() !== "" ? { 
              scale: 1.1,
              backgroundColor: "#2563eb"
            } : {}}
            whileTap={query.trim() !== "" ? { 
              scale: 0.95 
            } : {}}
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            exit={{ opacity: 0 }}
            transition={{
            repeat: Infinity,
            ease: "linear",
            duration: 2, // one full spin per 2s
            }}>
          <Boxes className="w-5 h-5" />
        </motion.button>
        )}

      </motion.div>

      {/* AI Hint */}
      <AnimatePresence>
        {!isFocused && query.trim() === "" && (
          <motion.div 
            className="absolute bottom-2 left-4 flex items-center text-xs text-gray-500"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 10, 0],
                scale: [1, 1.1, 1, 1.1, 1],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 5
              }}
            >
              <Sparkles className="w-4 h-4 mr-2 text-yellow-500" />
            </motion.div>
            <span>Pro Tip: Be specific to get better results</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TaskSearchBar;